import { App } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { operatorKeys, type InboxItem, type InboxMessage } from '@entities/operator';
import { extractErrorMessage, httpClient } from '@shared/api';
import { ChBadge } from '../channels/chUi';
import { Feed, statusText, time } from './chatParts';
import { SETUP_KEY, type Setup } from './OperatorsSetup';
import '../channels/channels.css';

const ME_KEY = 'richat.operator.me';

function readMe(): string | null {
  try {
    return window.localStorage.getItem(ME_KEY);
  } catch {
    return null;
  }
}

/** «Запросы»: клиенты, позвавшие человека, — список слева, переписка справа. */
export function OperatorsInbox({ onOpenSetup }: { onOpenSetup: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [me, setMe] = useState<string | null>(readMe);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  // Поле ввода в одну строку, растёт вверх под длину текста (как в тест-чате).
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, openId]);

  const setup = useQuery<Setup>({
    queryKey: SETUP_KEY,
    queryFn: async () => (await httpClient.get<Setup>('/operators/setup')).data,
  });
  const list = useQuery<InboxItem[]>({
    queryKey: operatorKeys.inbox,
    queryFn: async () => (await httpClient.get<InboxItem[]>('/operators/inbox')).data,
    refetchInterval: 3000,
  });
  const chat = useQuery<{ item: InboxItem; messages: InboxMessage[] }>({
    queryKey: operatorKeys.session(openId ?? ''),
    queryFn: async () => (await httpClient.get(`/operators/inbox/${openId}`)).data,
    enabled: !!openId,
    refetchInterval: 2000,
  });

  const operators = setup.data?.operators ?? [];
  const meOp = operators.find((o) => o.id === me) ?? operators[0];
  const items = useMemo(() => list.data ?? [], [list.data]);
  const msgCount = chat.data?.messages.length ?? 0;

  // Новый запрос или новое сообщение — строка всплывает наверх и вспыхивает.
  const seenAt = useRef<Map<string, string | null> | null>(null);
  const [flash, setFlash] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!list.data) return;
    const prev = seenAt.current;
    const fresh = prev
      ? list.data.filter((i) => i.needs_reply && (!prev.has(i.id) || prev.get(i.id) !== i.last_at))
      : [];
    seenAt.current = new Map(list.data.map((i) => [i.id, i.last_at]));
    if (!fresh.length) return;
    setFlash(new Set(fresh.map((i) => i.id)));
    const t = window.setTimeout(() => setFlash(new Set()), 2500);
    return () => window.clearTimeout(t);
  }, [list.data]);

  // Открыть первый запрос, который ждёт ответа.
  useEffect(() => {
    if (!openId && items.length) setOpenId((items.find((i) => i.needs_reply) ?? items[0]).id);
  }, [items, openId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [msgCount, openId]);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: operatorKeys.inbox }),
      qc.invalidateQueries({ queryKey: operatorKeys.summary }),
      openId && qc.invalidateQueries({ queryKey: operatorKeys.session(openId) }),
    ]);
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const chooseMe = (id: string) => {
    setMe(id);
    try {
      window.localStorage.setItem(ME_KEY, id);
    } catch {
      /* не запомним — не страшно */
    }
  };

  const send = () => {
    const t = text.trim();
    if (!t || !openId || !meOp) return;
    void act(async () => {
      await httpClient.post(`/operators/inbox/${openId}/message`, {
        operator_id: meOp.id,
        text: t,
      });
      setText('');
    });
  };

  if (setup.data && operators.length === 0) {
    return (
      <div className="op-empty">
        <p>Сначала добавьте оператора — его имя увидит клиент.</p>
        <button type="button" className="v2-btn is-primary" onClick={onOpenSetup}>
          Перейти в настройку
        </button>
      </div>
    );
  }

  const item = chat.data?.item;
  const closed = item?.status === 'closed';
  const othersChat = item?.status === 'active' && item.operator && item.operator !== meOp?.name;

  return (
    <div className="op-inbox">
      <aside className="op-list" aria-label="Запросы клиентов">
        {items.length === 0 && (
          <div className="op-list-empty">
            Запросов пока нет. Когда клиент позовёт человека или пришлёт файл, он появится здесь.
          </div>
        )}
        {items.map((i) => (
          <button
            key={i.id}
            type="button"
            className={[
              'op-li',
              i.id === openId && 'is-open',
              i.status === 'closed' && 'is-closed',
              i.needs_reply && 'is-new',
              flash.has(i.id) && 'is-flash',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setOpenId(i.id)}
          >
            <span className="op-li-ava" title={i.channel}>
              <ClientAvatar item={i} />
              {i.needs_reply && <span className="op-dot" aria-label="Ждёт ответа" />}
            </span>
            <span className="op-li-body">
              <span className="op-li-top">
                <b>{i.client}</b>
                <span className="op-li-time">{time(i.last_at)}</span>
              </span>
              <span className="op-li-text">{i.last_text}</span>
              <span className="op-li-meta">
                #{i.short} · {statusText(i)}
              </span>
            </span>
          </button>
        ))}
      </aside>

      <section className="op-chat" aria-label="Переписка">
        {!item ? (
          <div className="op-chat-empty">{items.length ? 'Выберите запрос слева' : ''}</div>
        ) : (
          <>
            <header className="op-chat-head">
              <div className="op-chat-who">
                <b>{item.client}</b>
                <span className="v2-small v2-muted">
                  #{item.short} · {item.channel} · {item.assistant} · {statusText(item)}
                </span>
              </div>
              {item.status === 'waiting' && meOp && (
                <button
                  type="button"
                  className="v2-btn is-primary"
                  disabled={busy}
                  onClick={() =>
                    act(() =>
                      httpClient.post(`/operators/inbox/${item.id}/accept`, {
                        operator_id: meOp.id,
                      }),
                    )
                  }
                >
                  Принять
                </button>
              )}
              {!closed && (
                <button
                  type="button"
                  className="tc-btn"
                  disabled={busy}
                  onClick={() => act(() => httpClient.post(`/operators/inbox/${item.id}/close`))}
                >
                  Завершить
                </button>
              )}
            </header>

            <div className="tc-scroll">
              <div className="tc-feed">
                <Feed messages={chat.data?.messages ?? []} item={item} />
                <div ref={bottom} />
              </div>
            </div>

            {closed ? (
              <div className="op-foot-note">
                Диалог завершён — дальше клиенту отвечает ассистент.
              </div>
            ) : othersChat ? (
              <div className="op-foot-note">Диалог ведёт {item.operator}.</div>
            ) : (
              <div className="tc-input-zone">
                <div className="tc-input-wrap">
                  <div className="tc-input op-input-line">
                    <label htmlFor="op-msg" className="v2-sr">
                      Ответ клиенту
                    </label>
                    <textarea
                      id="op-msg"
                      ref={inputRef}
                      rows={1}
                      value={text}
                      placeholder={`Ответ клиенту — он увидит «${meOp?.name ?? 'Оператор'}»`}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                          e.preventDefault();
                          send();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="tc-send"
                      aria-label="Отправить"
                      title="Отправить"
                      onClick={send}
                      disabled={busy || !text.trim()}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    </button>
                  </div>
                  <div className="tc-hint op-hint-row">
                    {operators.length > 1 && (
                      <label>
                        Отвечаю как{' '}
                        <select value={meOp?.id} onChange={(e) => chooseMe(e.target.value)}>
                          {operators.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                        {' · '}
                      </label>
                    )}
                    Enter — отправить, Shift+Enter — новая строка
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/** Фото клиента из мессенджера со значком канала в уголке; нет фото — значок канала. */
function ClientAvatar({ item }: { item: InboxItem }) {
  const [broken, setBroken] = useState(false);
  const kind = item.platform as 'widget' | 'max' | 'telegram' | 'api';
  if (!item.avatar || broken) return <ChBadge kind={kind} size={32} />;
  return (
    <span className="op-ava">
      <img src={item.avatar} alt="" onError={() => setBroken(true)} referrerPolicy="no-referrer" />
      <span className="op-ava-ch">
        <ChBadge kind={kind} size={16} />
      </span>
    </span>
  );
}
